import {
    Box,
    ClickAwayListener,
    IconButton,
    IconButtonProps,
    Paper,
    Popper,
    PopperPlacementType,
    Tooltip,
    Typography,
} from '@mui/material';
import { Cog, X } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

/**
 * MUI renders Menus, Selects and Dialogs into a portal under `.MuiModal-root`.
 * Clicks/Escape that land inside one of those overlays belong to it, not to
 * this panel, so they must not be treated as "dismiss the panel".
 */
const PORTAL_OVERLAY_SELECTOR = '.MuiModal-root';

interface PopperPanelProps {
    testID?: string;
    title: ReactNode;
    children: ReactNode;
    anchorRef?: React.RefObject<HTMLElement | null>;
    placement?: PopperPlacementType;
    width?: string | number;
    buttonTooltip?: ReactNode;
    /** Trigger icon; defaults to a cog. */
    icon?: ReactNode;
    /** Accessible label for the trigger button; defaults to "Properties". */
    ariaLabel?: string;
    /** Color of the trigger icon button. Defaults to "primary" (turquoise);
     * pass "default" to match neighboring plain icon buttons (gray). */
    color?: IconButtonProps['color'];
    xOffset?: number;
    yOffset?: number;
}

export default function PopperPanel({
    testID,
    title,
    children,
    anchorRef,
    placement = 'right-start',
    width = '300px',
    buttonTooltip = <FormattedMessage id="properties" />,
    icon = <Cog size={12} />,
    ariaLabel,
    color = 'primary',
    xOffset = 16,
    yOffset = -16.5,
}: PopperPanelProps) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    // Anchor the panel to an explicit element when one is provided, otherwise
    // to our own trigger button so callers can drop <PopperPanel> in without
    // wiring up a ref.
    const anchorEl = anchorRef?.current ?? buttonRef.current;

    // Close on Escape for keyboard accessibility (docs/ui_specs.md). When an
    // inner overlay (e.g. a Menu/Select opened from within the panel) is open,
    // let it consume Escape first instead of closing the whole panel.
    useEffect(() => {
        if (!open) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            // Only close when Escape didn't land inside an overlay opened from
            // within the panel (e.g. a Menu/Select) — that overlay handles its
            // own Escape first.
            if (
                event.key === 'Escape' &&
                !target?.closest?.(PORTAL_OVERLAY_SELECTOR)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    return (
        <Box display="flex">
            <Tooltip title={buttonTooltip}>
                <IconButton
                    ref={buttonRef}
                    color={color}
                    size="small"
                    data-testid={testID}
                    aria-controls="popper-panel"
                    aria-haspopup="true"
                    aria-label={
                        ariaLabel ?? intl.formatMessage({ id: 'properties' })
                    }
                    onClick={() => setOpen(!open)}
                    sx={{
                        backgroundColor: open
                            ? color === 'primary'
                                ? 'rgba(0,165,185,0.08)'
                                : 'action.selected'
                            : 'inherit',
                    }}
                >
                    {icon}
                </IconButton>
            </Tooltip>
            <Popper
                id="popper-panel"
                open={open && anchorEl !== null}
                anchorEl={anchorEl}
                placement={placement}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [yOffset, xOffset],
                        },
                    },
                    {
                        name: 'flip',
                        enabled: false,
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 0,
                        },
                    },
                ]}
                sx={{
                    zIndex: 1400,
                }}
            >
                <ClickAwayListener
                    onClickAway={(event) => {
                        const target = event.target as HTMLElement;
                        // Keep the panel open when the click lands inside a
                        // portaled overlay opened from within it, or directly
                        // on <body>.
                        if (
                            target.localName === 'body' ||
                            target.closest(PORTAL_OVERLAY_SELECTOR)
                        ) {
                            event.preventDefault();
                            return;
                        }
                        setOpen(false);
                    }}
                >
                    <Paper
                        sx={(theme) => ({
                            width,
                            padding: 2,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            borderLeft: 'none',
                        })}
                        elevation={0}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <Typography fontSize={14}>{title}</Typography>
                            <IconButton
                                size="small"
                                onClick={() => setOpen(false)}
                                sx={{ p: 0.25 }}
                                aria-label={intl.formatMessage({ id: 'close' })}
                            >
                                <X size={16} />
                            </IconButton>
                        </Box>
                        {children}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}
